import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Campo de fecha del panel. Mismo aspecto que el resto de inputs del
 * formulario, pero con el calendario de ng-bootstrap en vez del selector
 * nativo de `<input type="date">`, que lo pinta el navegador con su propio
 * estilo y no se puede tematizar.
 *
 * Habla el mismo valor que los formularios y la API: la cadena `yyyy-MM-dd`
 * (o vacía si no hay fecha), así que sustituye al input nativo sin tocar el
 * `FormGroup` que lo usa.
 */
@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateFieldComponent), multi: true }
  ]
})
export class DateFieldComponent implements ControlValueAccessor {

  @Input() placeholder: string = 'dd/mm/aaaa';

  /**
   * `field` pinta la caja del campo (formularios del panel). `plain` la quita
   * para cuando el hueco ya lo dibuja el contenedor de alrededor, como los
   * `.form-input-container` de los asistentes públicos.
   */
  @Input() appearance: 'field' | 'plain' = 'field';

  /**
   * Límites del calendario, en `yyyy-MM-dd`. Los valores por defecto son
   * deliberadamente amplios: con `navigation="select"` el desplegable de años
   * solo llega hasta donde llegan los límites, y un rango estrecho dejaría
   * fuera cosas tan normales como una fecha de nacimiento.
   */
  @Input() set min(value: string) { this.minDate = this.toStruct(value) || DateFieldComponent.DEFAULT_MIN; }
  @Input() set max(value: string) { this.maxDate = this.toStruct(value) || DateFieldComponent.DEFAULT_MAX; }

  private static readonly DEFAULT_MIN: NgbDateStruct = { year: 1900, month: 1, day: 1 };
  private static readonly DEFAULT_MAX: NgbDateStruct = { year: new Date().getFullYear() + 10, month: 12, day: 31 };

  minDate: NgbDateStruct = DateFieldComponent.DEFAULT_MIN;
  maxDate: NgbDateStruct = DateFieldComponent.DEFAULT_MAX;

  value: NgbDateStruct | null = null;
  disabled: boolean = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  /** Lo que ve el usuario en el campo: dd/mm/aaaa. */
  get display(): string {
    if (!this.value) return '';
    return `${this.pad(this.value.day)}/${this.pad(this.value.month)}/${this.value.year}`;
  }

  /**
   * Mes que abre el calendario cuando el campo está vacío: el más cercano al
   * rango permitido, para no abrir en un año desde el que no se puede navegar.
   */
  get startDate(): NgbDateStruct {
    if (this.value) return this.value;
    const today = new Date();
    const current: NgbDateStruct = { year: today.getFullYear(), month: today.getMonth() + 1, day: 1 };
    if (this.isAfter(current, this.maxDate)) return this.maxDate;
    if (this.isAfter(this.minDate, current)) return this.minDate;
    return current;
  }

  writeValue(value: string): void {
    this.value = this.toStruct(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onSelect(date: NgbDateStruct): void {
    this.value = date;
    this.onChange(`${date.year}-${this.pad(date.month)}-${this.pad(date.day)}`);
    this.onTouched();
  }

  /** El aspa vacía el campo sin abrir el calendario. */
  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.value = null;
    this.onChange('');
    this.onTouched();
  }

  private toStruct(value: string): NgbDateStruct | null {
    if (!value) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
  }

  private isAfter(a: NgbDateStruct, b: NgbDateStruct): boolean {
    return a.year !== b.year ? a.year > b.year
      : a.month !== b.month ? a.month > b.month
        : a.day > b.day;
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
}
